import { useParams, Link } from 'react-router-dom';
import { articlesByCategory } from '@/mocks/articles';
import { categories, categoryMap } from '@/mocks/categories';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

const articleImageUrls: Record<string, string> = {
  'anwang91-latest-access-address': 'https://readdy.ai/api/search-image?query=A%20dark%20digital%20tunnel%20with%20glowing%20onion%20layers%20representing%20Tor%20network%20encryption%2C%20abstract%20cybersecurity%20concept%2C%20warm%20amber%20and%20dark%20tones%2C%20minimalist%20digital%20art%20style&width=800&height=400&seq=article-1&orientation=landscape',
  'tor-browser-install-guide': 'https://readdy.ai/api/search-image?query=A%20clean%20minimalist%20digital%20workspace%20showing%20Tor%20browser%20interface%20concept%2C%20soft%20green%20and%20warm%20lighting%2C%20modern%20tech%20aesthetic%2C%20abstract%20privacy%20protection%20theme&width=800&height=400&seq=article-2&orientation=landscape',
  'anwang91-registration-guide': 'https://readdy.ai/api/search-image?query=An%20abstract%20digital%20registration%20interface%20with%20lock%20icons%20and%20shield%20symbols%2C%20warm%20amber%20accents%20on%20dark%20background%2C%20cybersecurity%20aesthetic%2C%20clean%20modern%20design&width=800&height=400&seq=article-3&orientation=landscape',
  'darknet-trading-security': 'https://readdy.ai/api/search-image?query=Abstract%20digital%20transaction%20security%20concept%20with%20shield%20and%20crypto%20symbols%2C%20warm%20green%20and%20amber%20tones%2C%20modern%20minimalist%20financial%20security%20design&width=800&height=400&seq=article-4&orientation=landscape',
  'identify-darknet-scams': 'https://readdy.ai/api/search-image?query=A%20warning%20sign%20concept%20with%20digital%20phishing%20hook%20and%20shield%2C%20warm%20red%20and%20amber%20accents%2C%20cybersecurity%20awareness%20design%2C%20clean%20minimalist%20style&width=800&height=400&seq=article-5&orientation=landscape',
  'darknet-malware-defense': 'https://readdy.ai/api/search-image?query=Digital%20virus%20defense%20concept%20with%20shield%20blocking%20malicious%20code%2C%20warm%20green%20glow%2C%20cybersecurity%20protection%20aesthetic%2C%20modern%20minimalist%20tech%20design&width=800&height=400&seq=article-6&orientation=landscape',
  'anonymous-communication-best-practices': 'https://readdy.ai/api/search-image?query=Abstract%20anonymous%20communication%20concept%20with%20encrypted%20data%20streams%20flowing%20through%20layered%20nodes%2C%20warm%20amber%20and%20green%20tones%2C%20modern%20privacy%20design&width=800&height=400&seq=article-7&orientation=landscape',
  'data-encryption-techniques': 'https://readdy.ai/api/search-image?query=Digital%20encryption%20visualization%20with%20data%20transforming%20into%20encrypted%20blocks%2C%20warm%20green%20and%20dark%20tones%2C%20modern%20cryptography%20aesthetic%2C%20minimalist%20tech%20design&width=800&height=400&seq=article-8&orientation=landscape',
  'anwang91-platform-review': 'https://readdy.ai/api/search-image?query=A%20professional%20platform%20review%20dashboard%20concept%20with%20star%20ratings%20and%20analytics%2C%20warm%20amber%20accents%2C%20modern%20business%20review%20aesthetic%2C%20clean%20design&width=800&height=400&seq=article-9&orientation=landscape',
  'darknet-market-comparison': 'https://readdy.ai/api/search-image?query=A%20comparison%20chart%20concept%20showing%20multiple%20platforms%20side%20by%20side%2C%20warm%20amber%20and%20green%20highlights%2C%20modern%20analytics%20design%2C%20minimalist%20data%20comparison%20aesthetic&width=800&height=400&seq=article-10&orientation=landscape',
  'darknet-security-trends-2026': 'https://readdy.ai/api/search-image?query=Futuristic%20cybersecurity%20threat%20landscape%20with%20AI%20nodes%20and%20quantum%20patterns%2C%20warm%20amber%20warning%20accents%2C%20modern%20data%20visualization%20design&width=800&height=400&seq=article-11&orientation=landscape',
  'global-darknet-regulation-updates': 'https://readdy.ai/api/search-image?query=Global%20regulation%20concept%20with%20world%20map%20and%20legal%20documents%2C%20warm%20green%20and%20amber%20tones%2C%20modern%20policy%20analysis%20design&width=800&height=400&seq=article-12&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;
  const categoryArticles = slug ? articlesByCategory[slug] || [] : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </span>
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
              分类未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              您访问的分类页面不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 text-sm"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 暗网91安全指南`,
    description: category.description,
    url: `https://www.anwang91.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '暗网91安全指南',
      url: 'https://www.anwang91.com',
    },
  };

  return (
    <>
      <SEOMeta
        title={`${category.name} - 暗网91安全指南`}
        description={category.description}
        keywords={`暗网91,${category.name},暗网安全,深网资源,隐私保护`}
        canonicalUrl={`https://www.anwang91.com/category/${slug}`}
        schemaJson={pageSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="flex items-center gap-4 mt-4">
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100">
                <i className={`${category.icon} text-2xl text-primary-600`}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 py-5 border-b border-background-200/70">
              <span className="text-sm text-foreground-500 mr-2">全部分类：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 py-8">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    category={article.category}
                    categoryName={category.name}
                    author={article.author}
                    publishDate={article.publishDate}
                    readTime={article.readTime}
                    imageUrl={articleImageUrls[article.slug] || ''}
                    tags={article.tags}
                  />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-list-3-line text-3xl text-foreground-300"></i>
                </span>
                <p className="text-sm text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}